import { useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { breakpoints } from 'styles';
import { getMessageWithPlaceholders } from 'shared-locales';
import { getAttributesWithMergedOptions } from 'shared-helpers/customAttributes';
import { Alert, Button, ModalButtonWithForm } from 'components/common';
import {
  ArrayField,
  Datepicker,
  FormGroup,
  Select,
  Text,
  TextArea
} from 'components/common/form';
import { UsersListSelectedUsers } from '.';

import type { FormApi, FormState } from 'informed';
import type { UpdateUserObject } from './Users';
import type { SelectedUser } from './UsersListSelectedUsers';

type Attribute = { key: string; value: string };
type FormValues = { attributes: Attribute[] };

interface Props {
  handleSuccess: (updateUser: UpdateUserObject) => Promise<void>;
}

const UsersUpdateInBatch = ({ handleSuccess }: Props) => {
  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    serverConfig,
    user: loggedInUser
  } = useReduxState();

  const formApi = useRef<FormApi<FormValues>>(undefined);

  const editableUserCustomAttributes = getAttributesWithMergedOptions({
    language,
    onlyEditable: true,
    requestUser: loggedInUser,
    userCustomAttributes: serverConfig.userCustomAttributes
  });

  const [showExpirationDateWarning, setShowExpirationDateWarning] =
    useState(false);
  const [users, setUsers] = useState<SelectedUser[]>([]);
  const [visible, setVisible] = useState(false);

  const handleHide = () => {
    setVisible(false);
    setShowExpirationDateWarning(false);
  };

  const handleRemove = () => {
    if (!formApi.current) return;
    const attributes: Attribute[] = formApi.current.getValue('attributes');

    let hasExpirationDate = false;
    attributes.forEach((attr) => {
      if (attr.key === 'expirationDate') hasExpirationDate = true;
    });
    if (!hasExpirationDate) setShowExpirationDateWarning(false);
  };

  const handleExpirationDateChange = (value) => {
    setShowExpirationDateWarning(Boolean(value));
  };

  const submit = async (values: FormValues) => {
    const userAttributes = { customAttributes: {} } as { [key: string]: any };
    (values.attributes as Attribute[]).forEach((attribute) => {
      if (attribute.key === 'expirationDate') {
        userAttributes.expirationDate = attribute.value || null;
      } else {
        userAttributes.customAttributes[attribute.key] = attribute.value;
      }
    });

    const response = await api.put('/admin/users-update-in-batch', {
      ids: users.map((user) => user.id),
      ...userAttributes
    });

    await handleSuccess({});

    dispatch(
      actions.addNotice({
        text: getMessageWithPlaceholders({
          message: messages.usersEditUsersSuccess,
          placeholders: { numberOfUsers: response.data.numberOfModifiedUsers }
        }),
        type: 'success'
      })
    );
  };

  const initialValues = { attributes: [{ key: '', value: '' }] } as FormValues;

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="edit"
      buttonIsDropdown={true}
      buttonStyle={{ whiteSpace: 'nowrap' }}
      buttonText={messages.usersUpdateMultipleUsers}
      initialValues={initialValues}
      modalActionText={messages.commonSave}
      modalTitle={messages.usersUpdateMultipleUsers}
      modalWidth="wide"
      onHide={handleHide}
      onShow={() => setVisible(true)}
      onSubmit={submit}
      setFormApi={(form: FormApi<FormValues>) => (formApi.current = form)}
    >
      {(formState: FormState<FormValues>) => (
        <div>
          {showExpirationDateWarning ? (
            <Alert
              text={getMessageWithPlaceholders({
                message: messages.userExpireAccount,
                placeholders: {
                  numberOfDays: serverConfig.accountExpirationDeleteBufferInDays
                }
              })}
              type="warning-light"
            />
          ) : null}

          {visible ? (
            <UsersListSelectedUsers
              includeAdmins={loggedInUser?.roles.includes('superadmin')}
              onLoadedUsers={(users) => setUsers(users)}
            />
          ) : null}

          <FormGroup label={messages.usersUpdateMultipleUsersLabel}>
            <div className="attributes">
              <ArrayField field="attributes">
                {({ add }) => (
                  <>
                    <ArrayField.Items<{ key: string }>>
                      {({ remove, field, index, values }) => {
                        const attribute = editableUserCustomAttributes.find(
                          (userCustomAttribute) =>
                            userCustomAttribute.key === values?.key
                        );

                        return (
                          <div className="attribute">
                            <div className="attribute-key-label">
                              {messages.commonAttribute}
                            </div>
                            <div className="attribute-key">
                              <Select
                                field={`${field}.key`}
                                id={`edit-user-attributes-attribute-key-${index}`}
                                options={[
                                  {
                                    label: messages.commonExpirationDate,
                                    value: 'expirationDate'
                                  },
                                  ...editableUserCustomAttributes.map(
                                    (attribute) => ({
                                      label:
                                        attribute.titleTranslations?.[
                                          language
                                        ] || attribute.title,
                                      value: attribute.key
                                    })
                                  )
                                ].filter(
                                  (option) =>
                                    !formState.values?.attributes?.some(
                                      (attr) => attr.key === option.value
                                    )
                                )}
                              />
                            </div>
                            <div className="attribute-value-label">
                              {messages.commonValue}
                            </div>
                            <div className="attribute-value">
                              {values?.key === 'expirationDate' ? (
                                <Datepicker
                                  field={`${field}.value`}
                                  id="edit-user-attributes-attribute-value-expiration-date"
                                  initialValue={null}
                                  isClearable={true}
                                  onValueChange={handleExpirationDateChange}
                                  placeholder={messages.commonNoExpirationDate}
                                  style={{ width: 200 }}
                                />
                              ) : attribute?.type === 'Boolean' ? (
                                <Select
                                  field={`${field}.value`}
                                  id={`edit-user-attributes-attribute-value-boolean-${index}`}
                                  initialValue={true}
                                  options={[
                                    {
                                      label: messages.commonYes,
                                      value: true
                                    },
                                    { label: messages.commonNo, value: false }
                                  ]}
                                  style={{ maxWidth: '100%', width: 120 }}
                                />
                              ) : attribute?.type === 'Number' ? (
                                <Select
                                  field={`${field}.value`}
                                  id={`edit-user-attributes-attribute-value-number-${index}`}
                                  initialValue={attribute.min}
                                  isSearchable={false}
                                  options={[
                                    ...[
                                      // @ts-ignore
                                      ...Array(
                                        attribute.max - attribute.min + 1
                                      ).keys()
                                    ].map((n) => ({
                                      label: n + attribute.min,
                                      value: n + attribute.min
                                    }))
                                  ]}
                                  style={{ maxWidth: '100%', width: 100 }}
                                />
                              ) : attribute?.type === 'Text' ? (
                                <TextArea
                                  field={`${field}.value`}
                                  initialValue=""
                                  style={{ maxWidth: '100%', width: 420 }}
                                />
                              ) : attribute?.type === 'Array' ||
                                attribute?.type === 'ArrayWithOptions' ? (
                                <Select
                                  field={`${field}.value`}
                                  id={`edit-user-attributes-attribute-value-array-${index}`}
                                  initialValue={[]}
                                  isSearchable={true}
                                  style={{ maxWidth: '100%', width: 420 }}
                                  {...(attribute?.type === 'ArrayWithOptions' &&
                                  attribute.mergedOptions
                                    ? {
                                        isMulti: true,
                                        options: [
                                          ...attribute.mergedOptions.map(
                                            (option) => ({
                                              label: option.label,
                                              value: option.key
                                            })
                                          )
                                        ]
                                      }
                                    : {
                                        creatable: true
                                      })}
                                />
                              ) : attribute?.type === 'StringWithOptions' ? (
                                <Select
                                  field={`${field}.value`}
                                  id={`edit-user-attributes-attribute-value-options-${index}`}
                                  initialValue={attribute.mergedOptions[0]}
                                  isSearchable={true}
                                  options={[
                                    ...attribute.mergedOptions.map(
                                      (option) => ({
                                        label: option.label,
                                        value: option.key
                                      })
                                    )
                                  ]}
                                  style={{ maxWidth: '100%', width: 300 }}
                                />
                              ) : (
                                <Text
                                  initialValue=""
                                  field={`${field}.value`}
                                  style={{ maxWidth: '100%', width: 300 }}
                                />
                              )}
                            </div>

                            {index !== 0 ? (
                              <div className="attribute-actions">
                                <Button
                                  aria-label={messages.commonDelete}
                                  category="light"
                                  data-tooltip={messages.commonDelete}
                                  icon="trash-alt"
                                  onClick={(attrs) => {
                                    remove();
                                    setTimeout(() => handleRemove());
                                  }}
                                  size="small"
                                  style={{ marginTop: 4 }}
                                />
                              </div>
                            ) : null}
                          </div>
                        );
                      }}
                    </ArrayField.Items>

                    <Button
                      category="light"
                      icon="plus"
                      onClick={add}
                      size="small"
                      style={{ marginTop: 15 }}
                      text={messages.attributesAdd}
                    />
                  </>
                )}
              </ArrayField>
            </div>
          </FormGroup>

          <style jsx>{`
            .attributes {
              margin-top: 10px;
            }
            .attribute {
              align-items: flex-start;
              display: flex;
              flex-direction: column;
            }
            .attribute:not(:first-child) {
              margin-top: 10px;
            }
            .attribute-key-label {
              margin: 10px 0 3px 0;
            }
            .attribute-key {
              flex-shrink: 0;
              margin-right: 20px;
              width: 240px;
            }
            .attribute-value-label {
              margin: 5px 0 3px 0;
            }
            @media (min-width: ${breakpoints.medium}px) {
              .attribute {
                flex-direction: row;
              }
              .attribute-key-label {
                display: none;
              }
              .attribute-value-label {
                margin: 7px 10px 0 0;
              }
              .attribute-value {
                margin-right: 10px;
              }
            }
          `}</style>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default UsersUpdateInBatch;
